import React from 'react';

import { Box, Grid, Typography } from '@mui/material';
import _ from 'es-toolkit/compat';
import { Form, FormSpy } from 'react-final-form';

import { type FormApi, useForm } from '@hrlogics/react-components/form';

import { Button } from 'src/components/common';
import { useFilter, useFilterStorage } from 'src/hooks/common';
import { DEFAULT_FILTER_CONTEXT_VALUE, FilterType } from 'src/providers/FilterProvider';
import { PaginationContextValue } from 'src/providers/PaginationProvider';
import type { SxProp } from 'src/types/mui';

import { TableFilterField } from './TableFilterField';
import type { TableFilter, TableFiltersGroup } from './types';

interface TableFiltersProps {
  tableFilters: TableFilter[];
  filtersGroups: TableFiltersGroup[];
  closePopover: () => void;
  isTrackFilter: boolean;
  pagination?: PaginationContextValue;
  keyFilter?: string;
}

export const TableFilters: React.FC<TableFiltersProps> = ({
  tableFilters,
  filtersGroups,
  closePopover,
  isTrackFilter,
  pagination,
  keyFilter,
}) => {
  const { filter, setFilter, setTableFilter } = useFilter();
  const {
    filter: filterByLocation,
    setFilter: setFilterByLocation,
    getFilterByKey,
    setFilterByKey,
  } = useFilterStorage();

  const initialValues = React.useMemo(() => {
    const values = {};
    const filterByKey = keyFilter && getFilterByKey(keyFilter);

    // eslint-disable-next-line no-restricted-syntax
    for (const tableFilter of tableFilters) {
      if (tableFilter.id.includes('.')) {
        const rootId = tableFilter.id.split('.')[0];
        if (rootId) {
          _.set(
            values,
            rootId,
            filterByKey ? filterByKey[rootId] : filterByLocation[rootId] || filter[rootId],
          );
        }
      } else {
        _.set(
          values,
          tableFilter.id,
          filterByKey
            ? filterByKey[tableFilter.id]
            : filterByLocation[tableFilter.id] ||
                filter[tableFilter.id] ||
                tableFilter.initialValue ||
                undefined,
        );
      }
    }

    return values;
  }, [filter, filterByLocation, getFilterByKey, keyFilter, tableFilters]);

  const onApply = React.useCallback(
    (values: Record<string, unknown>) => {
      setFilter(values);
      setTableFilter?.(DEFAULT_FILTER_CONTEXT_VALUE.tableFilter);
      if (keyFilter) {
        setFilterByKey(keyFilter, values);
      } else {
        setFilterByLocation(values);
      }
      pagination?.resetPage();
      closePopover();
    },
    [
      closePopover,
      keyFilter,
      pagination,
      setFilter,
      setFilterByKey,
      setFilterByLocation,
      setTableFilter,
    ],
  );

  const onCancel = React.useCallback(
    (form: FormApi<Record<string, unknown>>) => {
      setFilter(DEFAULT_FILTER_CONTEXT_VALUE.filter);
      if (keyFilter) {
        setFilterByKey(keyFilter, DEFAULT_FILTER_CONTEXT_VALUE.filter);
      } else {
        setFilterByLocation(DEFAULT_FILTER_CONTEXT_VALUE.filter);
      }
      form.restart();
      setTableFilter?.(DEFAULT_FILTER_CONTEXT_VALUE.tableFilter);
      closePopover();
    },
    [closePopover, keyFilter, setFilter, setFilterByKey, setFilterByLocation, setTableFilter],
  );

  const setTempFilters = React.useCallback(
    ({ values }: { values: FilterType }) => {
      if (isTrackFilter) {
        setTableFilter?.(values);
      }
    },
    [isTrackFilter, setTableFilter],
  );

  const renderFiltersFields = React.useCallback(() => {
    const groupedFilters = _.groupBy(tableFilters, 'groupId');

    return (
      <React.Fragment>
        {[emptyTableFiltersGroup, ...filtersGroups].map(filterGroup => {
          const { groupId, groupTitle, gridContainerProps } = filterGroup;
          const groupFilters = groupedFilters[groupId];

          if (!groupFilters) {
            return null;
          }

          return (
            <Grid key={groupId} container mt={1} spacing={1} {...gridContainerProps}>
              {groupTitle ? <Grid size={{ xs: 12 }}>{groupTitle}</Grid> : null}
              {groupFilters.map(filter => (
                <TableFilterField key={filter.id} tableFilter={filter} />
              ))}
            </Grid>
          );
        })}
      </React.Fragment>
    );
  }, [filtersGroups, tableFilters]);

  return (
    <Box sx={containerStyles}>
      <Typography variant="body2" fontSize={18} fontWeight={500}>
        Filter by
      </Typography>
      <Form initialValues={initialValues} initialValuesEqual={alwaysEqual} onSubmit={onApply}>
        {formProps => {
          return (
            <form onSubmit={formProps.handleSubmit}>
              <FormSpy subscription={{ values: true }} onChange={setTempFilters} />
              {renderFiltersFields()}
              <TableFiltersFormActions onCancel={onCancel} />
            </form>
          );
        }}
      </Form>
    </Box>
  );
};

type TableFiltersFormActionsProps = {
  onCancel: (form: FormApi<Record<string, unknown>>) => void;
};

// lives inside <Form> so it can reach the form api through context instead of a per-render closure
const TableFiltersFormActions: React.FC<TableFiltersFormActionsProps> = ({ onCancel }) => {
  const form = useForm<Record<string, unknown>>();
  const handleCancel = React.useCallback(() => onCancel(form), [form, onCancel]);

  return (
    <Box sx={bottomButtonsProps}>
      <Button onClick={handleCancel}>Cancel</Button>
      <Button type="submit">Apply filters</Button>
    </Box>
  );
};

const emptyTableFiltersGroup: TableFiltersGroup = {
  groupId: String(undefined),
};

// the popover re-mounts the form with the stored filters; a re-render must not reset edits
const alwaysEqual = () => true;

const containerStyles: SxProp = theme => ({
  width: 500,
  p: 2.5,
  pb: 1,
  [theme.breakpoints.down('sm')]: {
    width: '100%',
  },
});

const bottomButtonsProps: SxProp = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  gap: 1,
  mt: 2,
};
